import { useMemo } from 'react';

import { Box, CircularProgress } from '@mui/material';

import type {
  SortableTableUploadingRowProps,
  UploadingItem,
} from '@modules/_shared/components/sortable-list/types';
import { getColumnStyle } from '@modules/_shared/components/sortable-list/types';

const getCellContent = (
  column: SortableTableUploadingRowProps['columns'][number],
  item: UploadingItem,
) => {
  if (column.renderCell) {
    return column.renderCell({
      row: item,
      value: item[column.field as keyof UploadingItem],
      field: column.field,
    });
  }

  return String(item[column.field as keyof UploadingItem] ?? '');
};

export const SortableTableUploadingRow = ({
  item,
  columns,
  dragHandleWidth = 32,
}: SortableTableUploadingRowProps) => {
  const visibleColumns = useMemo(
    () => columns.filter(col => !col.hide),
    [columns],
  );

  return (
    <Box
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: '12px',
        py: 1,
        height: 65,
        backgroundColor: (theme.vars ?? theme).palette.background.paper,
        borderBottom: `1px solid ${(theme.vars ?? theme).palette.divider}`,
      })}
    >
      <Box
        sx={{
          width: dragHandleWidth,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={20} />
      </Box>

      {visibleColumns.map(column => (
        <Box
          key={column.field}
          sx={{
            ...getColumnStyle(column),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {getCellContent(column, item)}
        </Box>
      ))}
    </Box>
  );
};
